/* ----------------------------------------------------------------------------
 * AuraAudioPlayer — makes Aura actually SPEAK in the browser, reliably, across
 * the async gap between the hold-to-speak gesture and the reply audio arriving.
 *
 * WHY THIS EXISTS (bug fix 2026-07-18, iteration 2): the first playback fix used
 * an <audio> element fed by an object URL and a muted-play "prime" to unlock
 * autoplay. That still left Aura SILENT for two reasons:
 *   1. The muted prime ran on a SRC-LESS <audio> element (src is only set when
 *      the reply bytes arrive), so it primed nothing and established no durable
 *      user activation.
 *   2. The real .play() happens SECONDS after the gesture (STT POST -> AI assist
 *      POST -> TTS POST all await in between). By then the browser's transient
 *      user activation has expired (iOS Safari) / been consumed, so .play() is
 *      refused (NotAllowedError) and no sound comes out — while the orb had
 *      already been flipped to "speaking" optimistically.
 *
 * This iteration:
 *   - Uses a single shared Web Audio AudioContext. unlock()/prime() RESUMES it
 *     on the hold-to-speak gesture; a resumed AudioContext PERSISTS as 'running'
 *     and can start decoded buffers LATER without a fresh gesture — surviving the
 *     async gap. Playback decodes the MP3 via decodeAudioData -> BufferSource.
 *   - Keeps a plain <audio> element as a fallback for the rare environment with
 *     no Web Audio.
 *   - Binds "playing" to the ACTUAL start of audio (playBytes' onStart fires only
 *     after playback truly begins) and clears it on ended/stop/error — so the UI
 *     never shows "speaking" in silence.
 *   - Degrades gracefully: if playback is still blocked (NotAllowedError) it sets
 *     needsUserPlay + keeps the clip so the UI can offer a visible "tap to play".
 *   - Supports barge-in via stop().
 *
 * The DOM/Audio specifics live behind AudioPrimitive so this logic is unit-
 * testable under Node's --test. The default browser primitive is
 * createBrowserAudioPrimitive().
 * -------------------------------------------------------------------------- */


/** TTS output gain (owner #1, 2026-07-26): device tests found playback "quite
 *  high" on the PTT path (both paths share this player). 0.8 = modest cut;
 *  owner-tunable in one place. Range 0..1 (HTMLMediaElement.volume). */
export const TTS_PLAYBACK_VOLUME = 0.8;
export type PlayOutcome = 'played' | 'blocked' | 'skipped' | 'error';

/** Options for a single playback attempt. */
export interface PlayOptions {
  /** Fires EXACTLY when audio actually begins playing (not on request, not on a
   *  blocked attempt). Used to bind the visible "speaking" state to real audio. */
  onStart?: () => void;
}

/** The minimal audio sink the player drives. Injectable for tests. */
export interface AudioPrimitive {
  /** Stash raw audio bytes of the given MIME type for the next play(). */
  setBytes(bytes: Uint8Array, mime: string): void;
  /** Begin playback. Rejects with a DOMException-like { name } on failure;
   *  name === 'NotAllowedError' specifically means autoplay was blocked. */
  play(): Promise<void>;
  /** Prime the sink within a user gesture (resume the AudioContext) so a later
   *  play() is permitted across the async gap. May be a no-op on some primitives. */
  prime?(): Promise<void> | void;
  pause(): void;
  /** Stop, clear the source and release any object URL (keeps the context alive). */
  reset(): void;
  /** Register (or clear) the natural end-of-playback callback. */
  setOnEnded(cb: (() => void) | null): void;
}

export interface AuraAudioPlayerState {
  /** playback was blocked by the browser; a user gesture (replay) is required. */
  needsUserPlay: boolean;
  /** true while a clip is actively playing (bound to REAL playback, not request). */
  playing: boolean;
  /** playback has been unlocked by a user gesture at least once. */
  unlocked: boolean;
}

function isNotAllowed(err: unknown): boolean {
  return !!err && typeof err === 'object' && (err as { name?: string }).name === 'NotAllowedError';
}

export class AuraAudioPlayer {
  private readonly primitive: AudioPrimitive;
  private state: AuraAudioPlayerState = { needsUserPlay: false, playing: false, unlocked: false };
  private pending: { bytes: Uint8Array; mime: string } | null = null;
  private readonly listeners = new Set<(s: AuraAudioPlayerState) => void>();

  constructor(primitive: AudioPrimitive) {
    this.primitive = primitive;
    this.primitive.setOnEnded(() => {
      this.patch({ playing: false });
    });
  }

  getState(): AuraAudioPlayerState {
    return this.state;
  }

  /** Subscribe to state changes (for React re-render). Returns an unsubscribe. */
  subscribe(cb: (s: AuraAudioPlayerState) => void): () => void {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }

  private patch(next: Partial<AuraAudioPlayerState>): void {
    this.state = { ...this.state, ...next };
    for (const l of this.listeners) l(this.state);
  }

  /** Call on the push-to-talk user gesture to unlock browser autoplay (resume
   *  the shared AudioContext so a later reply can play across the async gap). */
  async unlock(): Promise<void> {
    try {
      await this.primitive.prime?.();
    } catch {
      /* priming best-effort — a blocked prime just means we may need a tap later */
    }
    if (!this.state.unlocked) this.patch({ unlocked: true });
  }

  /** Play synthesized audio bytes. Returns the outcome; never throws.
   *  opts.onStart fires ONLY when audio truly begins (binds "speaking" to sound). */
  async playBytes(bytes: Uint8Array, mime: string, opts?: PlayOptions): Promise<PlayOutcome> {
    if (!bytes || bytes.byteLength === 0) return 'skipped';
    this.pending = { bytes, mime };
    try {
      this.primitive.setBytes(bytes, mime);
      await this.primitive.play();
      // Audio is REALLY playing now — notify the caller so it can flip the
      // visible "speaking" state on the true start (never optimistically).
      opts?.onStart?.();
      this.patch({ playing: true, needsUserPlay: false });
      return 'played';
    } catch (err) {
      this.patch({ playing: false });
      if (isNotAllowed(err)) {
        // Autoplay blocked — keep the clip so the UI can offer "tap to play".
        this.patch({ needsUserPlay: true });
        return 'blocked';
      }
      // A decode/transport error (e.g. mock synthetic bytes): fail soft.
      return 'error';
    }
  }

  /** User-gesture retry of the last blocked clip. */
  async replay(opts?: PlayOptions): Promise<PlayOutcome> {
    if (!this.pending) return 'skipped';
    await this.unlock();
    const { bytes, mime } = this.pending;
    return this.playBytes(bytes, mime, opts);
  }

  /** Barge-in / stop: halt playback and drop any pending clip. */
  stop(): void {
    try {
      this.primitive.pause();
      this.primitive.reset();
    } catch {
      /* no-op */
    }
    this.pending = null;
    this.patch({ playing: false, needsUserPlay: false });
  }

  /** Release listeners + the sink (component unmount). */
  dispose(): void {
    this.stop();
    this.primitive.setOnEnded(null);
    this.listeners.clear();
  }
}

/** Minimal structural view of the Web Audio API we rely on (keeps this file
 *  free of lib.dom assumptions and testable with a fake global). */
interface MinimalAudioContext {
  state: string;
  destination: unknown;
  sampleRate: number;
  resume(): Promise<void>;
  decodeAudioData(buf: ArrayBuffer): Promise<MinimalAudioBuffer>;
  createBufferSource(): MinimalBufferSource;
  /** Gain node for output-level control (owner #1 volume, real Web Audio path). */
  createGain?(): MinimalGainNode;
  /** Allocate a raw PCM buffer (used for the silent keep-alive loop). */
  createBuffer(channels: number, length: number, sampleRate: number): MinimalAudioBuffer;
}
interface MinimalGainNode {
  readonly gain: { value: number };
  connect(dest: unknown): void;
  disconnect(): void;
}
interface MinimalAudioBuffer {
  readonly duration?: number;
}
interface MinimalBufferSource {
  buffer: MinimalAudioBuffer | null;
  onended: (() => void) | null;
  loop?: boolean;
  connect(dest: unknown): void;
  disconnect(): void;
  start(): void;
  stop(): void;
}
type AudioContextCtor = new () => MinimalAudioContext;

/** Default browser primitive: a shared, gesture-resumed Web Audio AudioContext
 *  (BufferSource playback that survives the async gap), with a plain <audio>
 *  element as fallback. Guarded so importing this module never throws under Node. */
export function createBrowserAudioPrimitive(): AudioPrimitive {
  const resolveCtor = (): AudioContextCtor | null => {
    const g = globalThis as unknown as {
      AudioContext?: AudioContextCtor;
      webkitAudioContext?: AudioContextCtor;
    };
    return g.AudioContext ?? g.webkitAudioContext ?? null;
  };

  let ctx: MinimalAudioContext | null = null;
  let ctorResolved = false;
  let ctorAvailable = false;
  let source: MinimalBufferSource | null = null;
  let pending: { bytes: Uint8Array; mime: string } | null = null;
  let endedCb: (() => void) | null = null;
  // Silent keep-alive: iOS Safari (and some Android Chrome states) auto-SUSPEND
  // an idle AudioContext during the seconds-long STT -> AI -> TTS gap, and opening
  // the mic (getUserMedia) can interrupt the audio session — after which resume()
  // is refused outside a fresh user gesture and EVERY reply segment would block.
  // Once the context is running (on a gesture) we start a looping buffer of pure
  // silence connected to the destination; an active source keeps the context
  // 'running' across the gap AND across the pauses between narration segments,
  // so all segments play without a second tap. It is inaudible (all-zero samples)
  // and independent of the real playback source, so it never disturbs decoding,
  // onended detection, or barge-in stop(). Best-effort: if it cannot start, the
  // tap-to-play fallback still guarantees no silent failure.
  let keepAlive: MinimalBufferSource | null = null;
  const startKeepAlive = (c: MinimalAudioContext): void => {
    if (keepAlive) return;
    try {
      const sr = typeof c.sampleRate === 'number' && c.sampleRate > 0 ? c.sampleRate : 22050;
      const buf = c.createBuffer(1, Math.max(1, Math.floor(sr)), sr); // ~1s of zeros
      const ka = c.createBufferSource();
      ka.buffer = buf;
      ka.loop = true;
      ka.connect(c.destination);
      ka.start();
      keepAlive = ka;
    } catch {
      /* keep-alive is best-effort; tap-to-play covers a hard block */
    }
  };

  // <audio> fallback (only used when Web Audio is unavailable).
  let el: HTMLAudioElement | null = null;
  let url: string | null = null;

  const ensureCtx = (): MinimalAudioContext | null => {
    if (ctx) return ctx;
    if (ctorResolved && !ctorAvailable) return null;
    ctorResolved = true;
    const Ctor = resolveCtor();
    if (!Ctor) {
      ctorAvailable = false;
      return null;
    }
    try {
      ctx = new Ctor();
      ctorAvailable = true;
      return ctx;
    } catch {
      ctorAvailable = false;
      return null;
    }
  };

  const ensureEl = (): HTMLAudioElement | null => {
    if (typeof Audio === 'undefined' && typeof document === 'undefined') return null;
    if (!el) {
      el = new Audio();
      el.volume = TTS_PLAYBACK_VOLUME; // owner #1 (2026-07-26): normalise output level
      el.preload = 'auto';
      el.addEventListener('ended', () => endedCb?.());
    }
    return el;
  };
  const revoke = (): void => {
    if (url && typeof URL !== 'undefined' && URL.revokeObjectURL) {
      try { URL.revokeObjectURL(url); } catch { /* no-op */ }
    }
    url = null;
  };
  const stopSource = (): void => {
    if (source) {
      // Null onended BEFORE stop so a barge-in restart does not fire a spurious end.
      source.onended = null;
      try { source.stop(); } catch { /* already stopped */ }
      try { source.disconnect(); } catch { /* no-op */ }
      source = null;
    }
  };
  const notAllowed = (): Error => {
    const e = new Error('autoplay blocked (context not running)');
    (e as { name: string }).name = 'NotAllowedError';
    return e;
  };

  return {
    setBytes(bytes: Uint8Array, mime: string): void {
      pending = { bytes, mime };
    },
    async play(): Promise<void> {
      const c = ensureCtx();
      if (c) {
        // ---- Web Audio path (robust across the async gap once resumed) ----
        if (c.state === 'suspended') {
          try { await c.resume(); } catch { /* fall through to the state check */ }
        }
        if (c.state !== 'running') {
          // Never resumed on a gesture — surface as blocked so the UI offers tap.
          throw notAllowed();
        }
        startKeepAlive(c); // hold the context open for the following segments/gaps
        if (!pending) return;
        stopSource();
        const b = pending.bytes;
        // Copy into a standalone ArrayBuffer (decodeAudioData detaches it).
        const ab = b.buffer.slice(b.byteOffset, b.byteOffset + b.byteLength) as ArrayBuffer;
        const buffer = await c.decodeAudioData(ab);
        const node = c.createBufferSource();
        node.buffer = buffer;
        // OWNER #1 VOLUME (IVG wave-2.4 F2 wiring, 2026-07-26): the PRIMARY Web
        // Audio path had NO gain stage, so el.volume on the <audio> fallback was
        // inaudible on modern browsers. Route source -> GainNode(0.8) ->
        // destination so the normalisation actually applies to spoken output.
        // Falls back to a direct connect if createGain is unavailable.
        const gain = typeof c.createGain === 'function' ? c.createGain() : null;
        if (gain) {
          gain.gain.value = TTS_PLAYBACK_VOLUME;
          node.connect(gain);
          gain.connect(c.destination);
        } else {
          node.connect(c.destination);
        }
        node.onended = () => { try { gain?.disconnect(); } catch { /* no-op */ } endedCb?.(); };
        source = node;
        node.start();
        return;
      }
      // ---- <audio> element fallback ----
      const a = ensureEl();
      if (!a) return;
      if (pending) {
        revoke();
        const blob = new Blob([pending.bytes as unknown as BlobPart], { type: pending.mime || 'audio/mpeg' });
        url = URL.createObjectURL(blob);
        a.src = url;
      }
      await a.play();
    },
    async prime(): Promise<void> {
      // Called INSIDE the hold-to-speak gesture. Resume the shared AudioContext
      // so a later decode+start (after the STT/AI/TTS gap) is permitted without a
      // fresh gesture. A resumed context persists as 'running'.
      const c = ensureCtx();
      if (c) {
        if (c.state === 'suspended') {
          try { await c.resume(); } catch { /* best effort — a tap may still be needed */ }
        }
        // A resumed context: pin it open for the async gap + inter-segment pauses.
        if (c.state === 'running') startKeepAlive(c);
        return;
      }
      // Element-fallback prime: muted play/pause, best-effort only.
      const a = ensureEl();
      if (!a) return;
      const wasMuted = a.muted;
      a.muted = true;
      try {
        await a.play();
        a.pause();
        try { a.currentTime = 0; } catch { /* no-op */ }
      } catch {
        /* no-op */
      } finally {
        a.muted = wasMuted;
      }
    },
    pause(): void {
      stopSource();
      el?.pause();
    },
    reset(): void {
      stopSource();
      if (el) {
        try { el.pause(); } catch { /* no-op */ }
        el.removeAttribute('src');
        try { el.load(); } catch { /* no-op */ }
      }
      revoke();
      pending = null;
      // Keep `ctx` alive + running so the NEXT reply plays without a new gesture.
    },
    setOnEnded(cb: (() => void) | null): void {
      endedCb = cb;
    },
  };
}
